/**
 * Collection Tabs AJAX
 * ====================
 * Custom element that handles AJAX loading of products when switching tabs.
 * Supports an "All" tab backed by one collection or an interleaved fallback.
 *
 * Features:
 * - AJAX loading of collection products on tab click
 * - Caching of fetched content to prevent redundant requests
 * - Optional dedicated collection for the "All" tab
 * - Interleaved "All" fallback from all tab collections
 * - Skeleton loading animation during fetch
 * - Re-initialization of quick add forms after AJAX content loads
 */

class CollectionTabsAjax extends HTMLElement {
  constructor() {
    super();

    this.sectionId = this.dataset.sectionId;
    this.showAllTab = this.dataset.showAllTab === 'true';
    this.allTabLabel = this.dataset.allTabLabel || 'All';
    this.allCollectionHandle = this.dataset.allCollectionHandle || '';
    this.productsToShow = parseInt(this.dataset.productsToShow, 10) || 8;

    // Parse collections data: "handle::label||handle::label"
    this.collections = [];
    const collectionsData = this.dataset.collections || '';
    if (collectionsData) {
      collectionsData.split('||').forEach(item => {
        const [handle, label] = item.split('::');
        if (handle) {
          this.collections.push({ handle, label });
        }
      });
    }

    // Cache for fetched content (keyed by collection handle or 'all')
    this.cache = new Map();

    // Current active tab
    this.activeTab = this.showAllTab ? 'all' : (this.collections[0]?.handle || null);

    // Elements
    this.tabNavigation = this.querySelector('.collection-tabs-ajax__nav');
    this.tabButtons = this.querySelectorAll('.collection-tabs-ajax__tab');
    this.productsContainer = this.querySelector('.collection-tabs-ajax__products');
    this.productsList = this.querySelector('.collection-tabs-ajax__products ul');
    this.skeleton = this.querySelector('.collection-tabs-ajax__skeleton');

    // State
    this.isLoading = false;

    this.init();
  }

  init() {
    // Cache initial content (either "all" or first collection)
    if (this.productsList && this.productsList.innerHTML.trim()) {
      this.cache.set(this.activeTab, this.productsList.innerHTML);
    }

    // Attach tab click listeners
    this.tabButtons.forEach(button => {
      button.addEventListener('click', this.handleTabClick.bind(this));
      button.addEventListener('focus', this.handleTabFocus.bind(this));
    });
  }

  /**
   * Keep a keyboard-focused tab fully visible without moving the document.
   * Adjust the horizontal tab rail only; scrollIntoView can escalate to page scroll.
   */
  handleTabFocus(event) {
    if (!this.tabNavigation) return;

    const button = event.currentTarget;
    const navRect = this.tabNavigation.getBoundingClientRect();
    const buttonRect = button.getBoundingClientRect();

    if (buttonRect.left < navRect.left) {
      this.tabNavigation.scrollLeft -= navRect.left - buttonRect.left;
    } else if (buttonRect.right > navRect.right) {
      this.tabNavigation.scrollLeft += buttonRect.right - navRect.right;
    }
  }

  async handleTabClick(event) {
    const button = event.currentTarget;
    const tabHandle = button.dataset.tabHandle;

    // Don't do anything if clicking already active tab or currently loading
    if (tabHandle === this.activeTab || this.isLoading) {
      return;
    }

    // Update active tab UI immediately
    this.tabButtons.forEach(function (btn) {
      btn.classList.remove('active');
      btn.setAttribute('aria-selected', 'false');
    });
    button.classList.add('active');
    button.setAttribute('aria-selected', 'true');

    // Update active tab state
    this.activeTab = tabHandle;

    // Check cache first
    if (this.cache.has(tabHandle)) {
      this.renderProducts(this.cache.get(tabHandle));
      return;
    }

    // Show skeleton and fetch content
    this.showSkeleton();

    try {
      let html;
      if (tabHandle === 'all') {
        html = this.allCollectionHandle
          ? await this.fetchCollection(this.allCollectionHandle)
          : await this.fetchAllCollections();
      } else {
        html = await this.fetchCollection(tabHandle);
      }

      // Cache the result
      this.cache.set(tabHandle, html);

      // Render products
      this.renderProducts(html);
    } catch (error) {
      console.error('Error fetching collection:', error);
      this.hideSkeleton();
    }
  }

  /**
   * Fetch products from a single collection
   * Uses Shopify's Section Rendering API
   */
  async fetchCollection(handle) {
    // Build URL to fetch the collection page with our section
    const url = `/collections/${handle}?section_id=${this.sectionId}&sort_by=best-selling`;

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch collection: ${response.status}`);
    }

    const text = await response.text();

    // Parse the response and extract just the product list items
    const parser = new DOMParser();
    const doc = parser.parseFromString(text, 'text/html');

    // Look for product cards in the response
    const productCards = doc.querySelectorAll('.collection-tabs-ajax__products ul li');

    if (productCards.length > 0) {
      // Return the HTML of the product cards
      let html = '';
      productCards.forEach(card => {
        html += card.outerHTML;
      });
      return html;
    }

    // Fallback: try to get products from a standard collection grid
    const gridItems = doc.querySelectorAll('.product-grid .grid__item');
    if (gridItems.length > 0) {
      let html = '';
      gridItems.forEach((item, index) => {
        if (index < this.productsToShow) {
          html += item.outerHTML;
        }
      });
      return html;
    }

    // If we can't find products, return empty
    return '';
  }

  /**
   * Fetch products from ALL collections in parallel
   * Interleaves results and removes duplicates
   */
  async fetchAllCollections() {
    // Fetch all collections in parallel
    const fetchPromises = this.collections.map(async (collection) => {
      const url = `/collections/${collection.handle}?section_id=${this.sectionId}&sort_by=best-selling`;

      try {
        const response = await fetch(url);
        if (!response.ok) {
          return { handle: collection.handle, products: [] };
        }

        const text = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');

        // Extract product cards
        let productCards = doc.querySelectorAll('.collection-tabs-ajax__products ul li');

        if (productCards.length === 0) {
          // Fallback to standard grid
          productCards = doc.querySelectorAll('.product-grid .grid__item');
        }

        const products = [];
        productCards.forEach((card, index) => {
          // Extract product handle from the card for deduplication
          const productLink = card.querySelector('a[href*="/products/"]');
          let productHandle = null;

          if (productLink) {
            const href = productLink.getAttribute('href');
            const match = href.match(/\/products\/([^?#/]+)/);
            if (match) {
              productHandle = match[1];
            }
          }

          products.push({
            html: card.outerHTML,
            handle: productHandle,
            index: index
          });
        });

        return { handle: collection.handle, products };
      } catch (error) {
        console.error(`Error fetching collection ${collection.handle}:`, error);
        return { handle: collection.handle, products: [] };
      }
    });

    const results = await Promise.all(fetchPromises);

    // Interleave products (2 from each collection per round) and deduplicate
    return this.interleaveProducts(results);
  }

  /**
   * Interleave products from multiple collections
   * Takes 2 products from each collection per round, removes duplicates
   */
  interleaveProducts(results) {
    const seenHandles = new Set();
    const interleavedHtml = [];
    const productsPerRound = 2;

    // Filter out collections with no products
    const validResults = results.filter(r => r.products.length > 0);

    if (validResults.length === 0) {
      return '';
    }

    // Track current index for each collection
    const indices = validResults.map(() => 0);

    let totalAdded = 0;
    let hasMoreProducts = true;

    while (totalAdded < this.productsToShow && hasMoreProducts) {
      hasMoreProducts = false;

      for (let collIndex = 0; collIndex < validResults.length; collIndex++) {
        const collection = validResults[collIndex];
        let addedThisRound = 0;

        while (addedThisRound < productsPerRound &&
               indices[collIndex] < collection.products.length &&
               totalAdded < this.productsToShow) {

          const product = collection.products[indices[collIndex]];
          indices[collIndex]++;

          // Skip duplicates
          if (product.handle && seenHandles.has(product.handle)) {
            continue;
          }

          if (product.handle) {
            seenHandles.add(product.handle);
          }

          interleavedHtml.push(product.html);
          totalAdded++;
          addedThisRound++;
        }

        // Check if any collection still has products
        if (indices[collIndex] < collection.products.length) {
          hasMoreProducts = true;
        }
      }
    }

    return interleavedHtml.join('');
  }

  /**
   * Show skeleton loading animation
   */
  showSkeleton() {
    this.isLoading = true;
    this.productsContainer.classList.add('is-loading');
    this.skeleton.classList.add('is-visible');
  }

  /**
   * Hide skeleton loading animation
   */
  hideSkeleton() {
    this.isLoading = false;
    this.productsContainer.classList.remove('is-loading');
    this.skeleton.classList.remove('is-visible');
  }

  /**
   * Render products with fade transition
   */
  renderProducts(html) {
    // Fade out
    this.productsContainer.style.opacity = '0';

    setTimeout(() => {
      // Update content
      this.productsList.innerHTML = html;

      // Re-initialize quick add forms
      this.reinitQuickAdd();

      // Re-initialize skeleton loader for new images
      this.reinitSkeletonLoader();

      // Re-initialize lift animation for new cards
      this.reinitLiftAnimation();

      // Hide skeleton
      this.hideSkeleton();

      // Fade in
      this.productsContainer.style.opacity = '1';
    }, 150);
  }

  /**
   * Re-initialize quick add forms after AJAX content load
   * This ensures the cart drawer integration works
   */
  reinitQuickAdd() {
    // Re-attach event listeners to new quick add forms
    const forms = this.querySelectorAll('.quick-add-form');

    forms.forEach(form => {
      // Remove any existing listeners (in case of re-render)
      const newForm = form.cloneNode(true);
      form.parentNode.replaceChild(newForm, form);

      newForm.addEventListener('submit', async function(e) {
        e.preventDefault();
        const btn = newForm.querySelector('button[type="submit"]');
        if (!btn || btn.disabled) return;

        const originalText = btn.textContent;
        btn.disabled = true;
        btn.textContent = 'Adding...';

        const variantId = newForm.querySelector('input[name="id"]').value;
        const cartDrawer = document.querySelector('cart-drawer');

        const body = {
          items: [{ id: parseInt(variantId), quantity: 1 }]
        };

        if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
          body.sections = cartDrawer.getSectionsToRender().map(function(s) { return s.id; }).join(',');
          body.sections_url = window.location.pathname;
        }

        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify(body)
          });
          const data = await response.json();

          if (data.status && data.status !== 200) {
            btn.textContent = 'Error';
            setTimeout(function() {
              btn.textContent = originalText;
              btn.disabled = false;
            }, 2000);
            return;
          }

          if (cartDrawer && data.sections && typeof cartDrawer.renderContents === 'function') {
            cartDrawer.renderContents(data);
          } else if (cartDrawer && typeof cartDrawer.open === 'function') {
            fetch('/?section_id=cart-drawer')
              .then(function(res) { return res.text(); })
              .then(function(html) {
                const parser = new DOMParser();
                const doc = parser.parseFromString(html, 'text/html');
                const newInner = doc.querySelector('.drawer__inner');
                const currentInner = document.querySelector('.drawer__inner');
                if (newInner && currentInner) {
                  currentInner.innerHTML = newInner.innerHTML;
                }
                cartDrawer.open();
              });
          }

          btn.textContent = 'Added!';
          setTimeout(function() {
            btn.textContent = originalText;
            btn.disabled = false;
          }, 1500);
        } catch (error) {
          console.error('Add to cart error:', error);
          btn.textContent = originalText;
          btn.disabled = false;
        }
      });
    });
  }

  /**
   * Re-initialize skeleton loader for new images
   */
  reinitSkeletonLoader() {
    if (window.SkeletonLoader && typeof window.SkeletonLoader.reinitSection === 'function') {
      window.SkeletonLoader.reinitSection(this);
    }
  }

  /**
   * Re-initialize lift animation for new cards
   */
  reinitLiftAnimation() {
    if (window.CardLiftAnimation && typeof window.CardLiftAnimation.reinit === 'function') {
      window.CardLiftAnimation.reinit(this.productsContainer);
    }
  }
}

// Register custom element
if (!customElements.get('collection-tabs-ajax')) {
  customElements.define('collection-tabs-ajax', CollectionTabsAjax);
}
